/**
 * PARTNERS SCREEN — matches mockup: filter chips, partner cards with deals,
 * type badges, phone/website actions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { partnersAPI, roadsAPI } from '../api';
import { T, fonts } from '../theme';
import LoadingSpinner from '../components/LoadingSpinner';
import BottomNav from '../components/BottomNav';

const typeColors = { lodging: T.info, transport: T.success, dining: '#E67E22', retail: T.copper, other: T.smoke };

export default function Partners() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [roads, setRoads] = useState([]);
  const [selectedRoad, setSelectedRoad] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    roadsAPI.getAll().then(res => {
      setRoads(res.data);
      if (res.data.length > 0) {
        setSelectedRoad(res.data[0]);
      }
    });
  }, []);

  useEffect(() => {
    if (selectedRoad) {
      setLoading(true);
      partnersAPI.getAll(selectedRoad.slug)
        .then(r => setPartners(r.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [selectedRoad]);

  const types = ['all', ...new Set(partners.map(p => p.category).filter(Boolean))];
  const filtered = filter === 'all' ? partners : partners.filter(p => p.category === filter);

  if (loading && !selectedRoad) return <LoadingSpinner />;

  return (
    <div style={{ background: '#f0ebe3', minHeight: '100vh', paddingBottom: 80, fontFamily: fonts.body }}>
      {/* Header */}
      <div style={{ background: '#1a2234', padding: '56px 20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontFamily: fonts.heading, fontSize: 22, fontWeight: 900, color: 'white' }}>Partners</div>
          <div style={{ fontSize: 12, color: T.smoke, marginTop: 4 }}>Deals & Booking for the Copper Road.</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button 
            type="button"
            onClick={() => window.location.href = '/map?from=partners'} 
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 20, padding: '6px 14px', color: T.cream, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
          >
            🗺️ Map
          </button>
          <div
            onClick={() => window.location.href = '/profile'}
            style={{
              width: 38, height: 38, borderRadius: '50%',
              background: '#ffffff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
              cursor: 'pointer', border: '2px solid #c48f4a',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s',
            }}
          >
            <span style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))' }}>👤</span>
          </div>
        </div>
      </div>

      {/* Road Selector */}
      {roads.length > 1 && (
        <div style={{ padding: '0 16px', background: '#1a2234', display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 16 }}>
          {roads.map(r => (
            <button
              key={r.slug}
              type="button"
              onClick={() => setSelectedRoad(r)}
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                border: selectedRoad?.slug === r.slug ? 'none' : '1px solid #b8753d',
                background: selectedRoad?.slug === r.slug ? '#b8753d' : 'transparent',
                color: selectedRoad?.slug === r.slug ? 'white' : '#b8753d',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: fonts.body,
                whiteSpace: 'nowrap'
              }}
            >
              {r.region}
            </button>
          ))}
        </div>
      )}

      {/* Filter chips */}
      <div style={{ padding: '16px 16px', display: 'flex', gap: 8, overflowX: 'auto' }}>
        {types.map(t => (
          <button key={t} type="button" onClick={() => setFilter(t)}
            style={{
              padding: '6px 16px',
              borderRadius: 20,
              border: filter === t ? 'none' : '1px solid #d5c8b5',
              background: filter === t ? '#b8753d' : 'transparent',
              color: filter === t ? 'white' : '#6b7280',
              fontFamily: fonts.body,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}>
            {t === 'all' ? 'All Partners' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Partner cards */}
      <div style={{ padding: '0 16px 28px' }}>
        {filtered.map(p => {
          const col = typeColors[p.category] || T.copper;
          const bgCol = p.category === 'lodging' ? '#eef4f9' : p.category === 'transport' ? '#e9f5ed' : '#fcf0e8';
          const icon = p.category === 'lodging' ? '🏨' : p.category === 'transport' ? '🚌' : p.category === 'dining' ? '🍽️' : '🤝';

          return (
            <div key={p.id} onClick={() => setSelectedPartner(p)}
              style={{ background: 'white', borderRadius: 16, padding: '16px', marginBottom: 12, border: '1px solid #e0d5c1', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', cursor: 'pointer' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 56, height: 56, borderRadius: 12, background: bgCol, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>{icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#1c1010' }}>{p.name}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: col, textTransform: 'capitalize', flexShrink: 0, marginLeft: 8 }}>{p.category}</div>
                  </div>
                  <div style={{ fontSize: 13, color: '#8e96a4', marginBottom: 8 }}>{p.address_city || p.city || ''}</div>
                  {p.description && (
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 10, lineHeight: 1.4 }}>{p.description}</div>
                  )}
                  {(p.is_deal_active == 1 && (p.deal_title || p.deal_description)) && (
                    <div style={{ background: '#FFF7ED', borderRadius: 12, padding: '12px 14px', fontSize: 13, color: '#9A3412', fontWeight: 600, border: '1px solid #FFEDD5', marginTop: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 16 }}>🎁</span>
                        <span style={{ fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase', color: '#C2410C' }}>Exclusive Deal</span>
                      </div>
                      {p.deal_title || p.deal_description}
                      {p.promo_code && (
                        <div style={{ marginTop: 8, padding: '6px 10px', background: 'white', borderRadius: 6, border: '1px dashed #FDBA74', display: 'inline-block', fontSize: 11, fontWeight: 'bold' }}>
                          PROMO: <span style={{ color: '#C2410C' }}>{p.promo_code}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                {p.phone && <div style={{ flex: 1, padding: '12px', background: '#f5f0e6', borderRadius: 10, textAlign: 'center', fontSize: 12, color: '#4A5568', fontWeight: 600, border: '1px solid #E2E8F0' }}>{p.phone}</div>}

                {p.booking_url ? (
                  <div onClick={(e) => { e.stopPropagation(); window.open(p.booking_url, '_blank'); }}
                    style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #B87333 0%, #8d5d36 100%)', borderRadius: 10, textAlign: 'center', fontSize: 12, color: 'white', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(184,115,51,0.2)' }}>
                    Book Now →
                  </div>
                ) : p.website ? (
                  <div onClick={(e) => { e.stopPropagation(); window.open(`https://${p.website.replace(/^https?:\/\//, '')}`, '_blank'); }}
                    style={{ flex: 1, padding: '12px', background: '#2D3748', borderRadius: 10, textAlign: 'center', fontSize: 12, color: 'white', fontWeight: 700, cursor: 'pointer' }}>
                    Visit Website →
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Partner detail modal */}
      {
        selectedPartner && (
          <div onClick={() => setSelectedPartner(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,26,31,0.7)', zIndex: 200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', backdropFilter: 'blur(3px)' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '22px 22px 0 0', padding: '0 0 32px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}><div style={{ width: 40, height: 4, borderRadius: 2, background: T.creamDark }} /></div>
              <div style={{ background: `linear-gradient(135deg,${T.ink},${T.inkMid})`, padding: '16px 20px 20px' }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <div style={{ width: 52, height: 52, borderRadius: 12, background: `${typeColors[selectedPartner.category] || T.copper}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>
                    {selectedPartner.category === 'lodging' ? '🏨' : selectedPartner.category === 'transport' ? '🚌' : '🍽️'}
                  </div>
                  <div>
                    <div style={{ fontFamily: fonts.heading, fontSize: 18, fontWeight: 700, color: T.cream }}>{selectedPartner.name}</div>
                    <div style={{ fontSize: 11, color: typeColors[selectedPartner.category] || T.copper, fontWeight: 600, marginTop: 3, textTransform: 'capitalize' }}>{selectedPartner.category}</div>
                  </div>
                </div>
              </div>
              <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {(selectedPartner.deal_title || selectedPartner.deal_description) && (
                  <div style={{ background: '#FEF4E7', borderRadius: 14, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ fontSize: 26 }}>🎁</div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: T.copperDark, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2 }}>Your Deal</div>
                      <div style={{ fontSize: 14, color: T.ink, fontWeight: 500 }}>{selectedPartner.deal_title || selectedPartner.deal_description}</div>
                    </div>
                  </div>
                )}
                <div style={{ background: T.cream, borderRadius: 14, overflow: 'hidden' }}>
                  {[
                    { icon: '📍', label: 'Address', val: selectedPartner.address || selectedPartner.address_line1 || '—' },
                    { icon: '📞', label: 'Phone', val: selectedPartner.phone || '—' },
                    { icon: '🌐', label: 'Website', val: selectedPartner.website || '—' },
                  ].map((r, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '11px 14px', borderBottom: i < 2 ? `1px solid ${T.creamDark}` : 'none' }}>
                      <span style={{ fontSize: 16 }}>{r.icon}</span>
                      <div>
                        <div style={{ fontSize: 10, color: T.smoke, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{r.label}</div>
                        <div style={{ fontSize: 13, color: i === 2 ? T.copper : T.ink, fontWeight: i === 2 ? 600 : 400 }}>{r.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={() => setSelectedPartner(null)} style={{ padding: 13, background: T.cream, border: 'none', borderRadius: 40, color: T.smoke, fontFamily: fonts.body, fontSize: 13, cursor: 'pointer' }}>Close</button>
              </div>
            </div>
          </div>
        )
      }

      <BottomNav active="/partners" />
    </div >
  );
}
